import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UploadDataType } from "@data-types/sow/PlayerSowTypes";
import { FormButton } from "@components/buttons";
import { useLocation } from "react-router-dom";
import { PlayerSowStoreContext } from "@state/context/sow/PlayerSowStoreContext";
import { FieldValues, useForm, FormProvider } from "react-hook-form";
import { CheckboxField } from "@components/inputs/mobile";
import { observer } from "mobx-react";
import {
	PlayerSowInfo,
	SowDocumentUploadSection,
	SowQuestionFields,
} from "@features/player-sow/mobile/components";
import { SowFilesSection } from ".";

export const SowSubmissionForm = observer(function SowSubmissionForm() {
	const { t } = useTranslation();

	const playerSowStore = useContext(PlayerSowStoreContext);

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			userName: playerSowStore?.rootStore.userAuthStore.user?.username,
		},
	});

	const { search } = useLocation();
	const queryParams = new URLSearchParams(search);

	const params: Record<string, string> = {};
	queryParams.forEach((value, key) => {
		params[key] = value;
	});

	if (!playerSowStore) {
		return null;
	}

	useEffect(() => {
		const element = document.getElementById("scroll-sow");
		if (element) {
			// timeout because otherwise it wont scroll
			setTimeout(() => {
				element.scrollIntoView({
					block: "end",
					behavior: "auto",
				});
			}, 100);
		}
	}, []);

	const handleSubmitForm = async (data: FieldValues) => {
		const submitParams: UploadDataType = {
			requestId: params.requestId,
			userId: params.userId,
		};

		await playerSowStore.onSubmitFullForm(data, submitParams);
	};

	return (
		<FormProvider {...methods}>
			<div id="scroll-sow"></div>
			<form
				id="uploadDropzone"
				className="card"
				onSubmit={methods.handleSubmit(
					async (data) => await handleSubmitForm(data)
				)}
			>
				<div className="card__body">
					<div className="card__header">
						<div className="card--primary__title title--primary">
							{t("PLAYER_SOW.TITLE")}
						</div>
					</div>
					<div className="u-type--base u-mar--top--xsml">
						{t("PLAYER_SOW.INFO")}
					</div>
					{playerSowStore.submissionStatusReason && (
						<div className="u-mar--top--xsml u-type--wgt--medium">
							<div>{t("PLAYER_SOW.REASON_TITLE")}</div>
							<div className="u-mar--top--xsml u-type--wgt--medium">
								{t("PLAYER_SOW.REASON_PARAGRAPH")}
							</div>
							<div className="u-color--text--secondary u-type--style--italic">
								"{playerSowStore.submissionStatusReason}"
							</div>
						</div>
					)}
					<PlayerSowInfo isFullForm={playerSowStore.isFormRequired} />
					<SowQuestionFields />
					<SowDocumentUploadSection />
					<SowFilesSection />
					<div className="u-type--base u-mar--top--xsml">
						<label
							htmlFor="sowTextarea"
							className="label u-type--wgt--medium"
						>
							{t("PLAYER_SOW.TEXTAREA_PLACEHOLDER")}
						</label>
						<textarea
							id="sowTextarea"
							maxLength={10000}
							className="input input--textarea"
							rows={4}
							/* placeholder={t("PLAYER_SOW.TEXTAREA_PLACEHOLDER")} */
							onChange={(e) =>
								playerSowStore.setNotes(e.target.value)
							}
						></textarea>
					</div>
				</div>
				<div className="card__footer">
					<CheckboxField
						name="confirmCheckbox"
						id="sowConfirmCheckbox"
						required
						label={t("PLAYER_SOW.CHECKBOX_TEXT")}
					/>
					<FormButton
						className="btn--base btn--block btn--accent card--primary__footer__btn"
						type="submit"
						btnText={t("PLAYER_SOW.SUBMIT_BUTTON")}
						disabled={playerSowStore.files.length == 0}
					/>
					<div className="card__footer">
						<span className="label">
							{t("PLAYER_SOW.DATA_COLLECTION")}
						</span>
					</div>
				</div>
			</form>
		</FormProvider>
	);
});
